import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { CreditCard, RotateCcw } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import CouponField from "./CouponField";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import PaymentIssueBanner from "./PaymentIssueBanner";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import {
    useCancelPlusSubscription,
    useChangePlusTier,
    useOpenBillingPortal,
    usePlusQuote,
    usePlusSubscription,
    useResumePlusSubscription,
    useCheckCoupon,
    useStartPlusCheckout,
} from "../../application/usePlusSubscription";
import {
    ANNUAL_DISCOUNT_PERCENT,
    BillingPeriod,
    Coupon,
    MAX_LINE,
    MAX_SEAT_LINE,
    ModuleOption,
    PlanTier,
    PlusSubscriptionState,
    Quote,
    PLUS_LINE,
    SEAT_LINE,
    addableModules,
    canSubscribe,
    heldTier,
    modulePriceFor,
    payableCents,
    isCancelling,
    quoteFor,
    trialChargeDate,
} from "../../domain/types";
import { checkoutErrorKey } from "../../domain/coupon";
// The catalogue's own copy, so a module is called the same thing here as on
// the screen somebody adopts it from. The server sends the database name,
// which is English whatever the reader speaks.
import { moduleName } from "../../../../shared/i18n/toolCopy";

const formatMoney = (cents: number, currency: string, language: string): string =>
    // Minor units to major, formatted for the reader's locale. The server
    // deliberately sends neither a formatted string nor a locale: it does not
    // know who is reading.
    new Intl.NumberFormat(language, { style: "currency", currency: currency.toUpperCase() })
        .format(cents / 100);

/**
 * What to call a line on the receipt.
 *
 * A module's line is keyed by its slug and labelled with the catalogue's own
 * name, which is English in the database whatever the reader speaks — so a
 * Spanish receipt said "Augmented Memory". The catalogue's copy is keyed by
 * that same slug, so the two screens now agree in both languages. The base
 * plan and a centre seat keep the label the server sent: "Plus" is "Plus".
 */
export const lineLabel = (
    t: TFunction,
    line: { key: string; label: string }
): string => {
    if (line.key === PLUS_LINE || line.key === MAX_LINE) return line.label;
    // "Seat" is the server's word for it, and the server has one language.
    if (line.key === SEAT_LINE) return t("subscription.billing.seatLine");
    if (line.key === MAX_SEAT_LINE) return t("subscription.billing.seatLineMax");
    return moduleName(t, { slug: line.key, name: line.label });
};

/**
 * What the charge is made of, line by line.
 *
 * Shown because a total that silently includes a module somebody adopted
 * months ago is a surprise on a card statement. Each line names itself, and a
 * line for several seats says how many.
 */
function QuoteBreakdown({
    quote,
    currency,
    language,
}: {
    quote: Quote;
    currency: string;
    language: string;
}) {
    const { t } = useTranslation();
    if (quote.lines.length === 0) return null;

    // Null where the server would not state a figure — a coupon tied to
    // particular prices, or a minimum this purchase has not reached. Then the
    // note under the field says the discount lands at payment, which is all
    // anybody can honestly promise.
    const discounted = quote.couponTotalCents ?? null;

    return (
        <div className="space-y-2 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            {quote.lines.map((line) => (
                <div key={line.key} className="flex justify-between gap-4 text-sm">
                    <span className="text-content-secondary">
                        {line.quantity > 1
                            ? t("subscription.billing.lineWithQuantity", {
                                  label: lineLabel(t, line),
                                  quantity: line.quantity,
                              })
                            : lineLabel(t, line)}
                    </span>
                    <span className="font-medium text-content-primary">
                        {formatMoney(line.totalCents, currency, language)}
                    </span>
                </div>
            ))}
            <div className="space-y-2 border-t border-gray-200 pt-2 dark:border-gray-700">
                {discounted !== null && (
                    /* The code's own line, so the total below it is arrived at
                       rather than asserted. */
                    <div className="flex justify-between gap-4 text-sm text-green-700 dark:text-green-400">
                        <span>
                            {t("subscription.billing.couponLine", {
                                code: quote.couponCode,
                            })}
                        </span>
                        <span className="font-medium">
                            −{formatMoney(quote.totalCents - discounted, currency, language)}
                        </span>
                    </div>
                )}
                <div className="flex justify-between gap-4 text-sm font-semibold text-content-primary">
                    <span>{t("subscription.billing.total")}</span>
                    <span className="flex items-baseline gap-2">
                        {discounted !== null && (
                            /* The undiscounted figure stays visible, struck
                               through: a total that simply dropped would leave
                               somebody wondering what it was before. */
                            <span className="text-caption font-normal text-content-tertiary line-through">
                                {formatMoney(quote.totalCents, currency, language)}
                            </span>
                        )}
                        <span>
                            {formatMoney(payableCents(quote), currency, language)}
                        </span>
                    </span>
                </div>
                {quote.couponCode && discounted === null && (
                    /* A code whose worth cannot be stated here — tied to
                       particular prices, or with a minimum this has not
                       reached. Saying so beats a figure nobody can stand
                       behind. */
                    <p className="text-xs text-content-tertiary">
                        {t("subscription.coupon.appliedAtPayment")}
                    </p>
                )}
                {/* Every figure here is before VAT: Stripe adds it at payment,
                    from the address and VAT number given there. */}
                <p className="text-xs text-content-tertiary">
                    {t("subscription.billing.tax.note")}
                </p>
            </div>
        </div>
    );
}

/**
 * The paid modules, ticked or not, as part of the purchase.
 *
 * Offered here because this is the only moment somebody upgrading from Basic
 * can take one: the catalogue is behind Plus, so until they have paid they
 * cannot adopt anything — and coming back for it afterwards means a second
 * checkout, a second invoice and a discount code that no longer applies.
 */
function ModulePicker({
    modules,
    selected,
    period,
    currency,
    language,
    disabled,
    onToggle,
}: {
    modules: ModuleOption[];
    selected: string[];
    period: BillingPeriod;
    currency: string;
    language: string;
    disabled: boolean;
    onToggle: (slug: string) => void;
}) {
    const { t } = useTranslation();
    if (modules.length === 0) return null;

    return (
        <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-content-primary">
                {t("subscription.billing.modules.title")}
            </legend>
            <p className="text-caption text-content-tertiary">
                {t("subscription.billing.modules.hint")}
            </p>

            <div className="space-y-2">
                {modules.map((module) => (
                    <label
                        key={module.slug}
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${
                            selected.includes(module.slug)
                                ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                : "border-gray-200 dark:border-gray-700"
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={selected.includes(module.slug)}
                            disabled={disabled}
                            onChange={() => onToggle(module.slug)}
                            className="mt-1 accent-brand-500"
                        />
                        <span className="flex flex-1 flex-wrap items-baseline justify-between gap-2">
                            <span className="text-sm font-medium text-content-primary">
                                {moduleName(t, { slug: module.slug, name: module.name })}
                            </span>
                            <span className="text-caption text-content-secondary">
                                {/* The figure for the period being bought, so a
                                    module does not quote a month beside a plan
                                    quoting a year. */}
                                {t(
                                    period === "annual"
                                        ? "subscription.billing.modules.perYear"
                                        : "subscription.billing.modules.perMonth",
                                    {
                                        price: formatMoney(
                                            modulePriceFor(module, period),
                                            currency,
                                            language
                                        ),
                                    }
                                )}{" "}
                                {t("subscription.billing.tax.suffix")}
                            </span>
                        </span>
                    </label>
                ))}
            </div>
        </fieldset>
    );
}

const formatDate = (date: Date, language: string): string =>
    new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(date);

const TIERS: PlanTier[] = ["plus", "max"];

/**
 * Plus or Max: the same plan, sized by sessions a month. Each option says
 * its own monthly price for the period being looked at, so the difference
 * is read rather than worked out.
 */
function TierPicker({
    state,
    tier,
    period,
    currency,
    language,
    disabled,
    onChange,
}: {
    state: PlusSubscriptionState;
    tier: PlanTier;
    period: BillingPeriod;
    currency: string;
    language: string;
    disabled: boolean;
    onChange: (tier: PlanTier) => void;
}) {
    const { t } = useTranslation();
    return (
        <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-content-primary">
                {t("subscription.billing.tier.legend")}
            </legend>
            <div className="flex flex-col gap-3 sm:flex-row">
                {TIERS.map((option) => {
                    // The plan's own line, per month: what the size costs,
                    // without the modules that cost the same at either size.
                    const base = quoteFor(state, period, option)?.lines[0]?.unitCents;
                    const perMonth =
                        base === undefined ? null : period === "annual" ? Math.floor(base / 12) : base;
                    return (
                        <label
                            key={option}
                            className={`flex flex-1 cursor-pointer items-start gap-3 rounded-xl border p-4 ${
                                tier === option
                                    ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                    : "border-gray-200 dark:border-gray-700"
                            }`}
                        >
                            <input
                                type="radio"
                                name="plan-tier"
                                value={option}
                                checked={tier === option}
                                disabled={disabled}
                                onChange={() => onChange(option)}
                                className="mt-1 accent-brand-500"
                            />
                            <span className="flex flex-col gap-0.5">
                                <span className="text-sm font-semibold text-content-primary">
                                    {t(`subscription.billing.tier.${option}.name`)}
                                </span>
                                <span className="text-sm text-content-secondary">
                                    {t(`subscription.billing.tier.${option}.sessions`)}
                                </span>
                                {perMonth !== null && (
                                    <span className="text-xs text-content-tertiary">
                                        {t("subscription.billing.perMonth", {
                                            price: formatMoney(perMonth, currency, language),
                                        })}{" "}
                                        {t("subscription.billing.tax.suffix")}
                                    </span>
                                )}
                            </span>
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );
}

/**
 * The account's Plus subscription, as one explicit branch per state.
 *
 * A switch rather than scattered ternaries: "which buttons show" is the whole
 * behaviour of this screen, and it has to be readable in one place. The five
 * states are the ones the API can report — never subscribed, Plus with nothing
 * to bill, active, cancelling, and payment failed.
 */
export default function PlusSubscriptionCard() {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const { data: state, isLoading, isError, refetch } = usePlusSubscription();
    const startCheckout = useStartPlusCheckout();
    const checkCoupon = useCheckCoupon();
    const cancel = useCancelPlusSubscription();
    const resume = useResumePlusSubscription();
    const portal = useOpenBillingPortal();
    const changeTier = useChangePlusTier();
    const [confirmingCancel, setConfirmingCancel] = useState(false);
    // The size being moved to, while its confirmation is open.
    const [confirmingTier, setConfirmingTier] = useState<PlanTier | null>(null);
    // Plus or Max for a purchase. A link from the plans page can ask for Max.
    const [tier, setTier] = useState<PlanTier>(() =>
        searchParams.get("tier") === "max" ? "max" : "plus"
    );
    // Annual first: it is the cheaper of the two per month, and defaulting to
    // the dearer one would be a strange thing to recommend.
    const [period, setPeriod] = useState<BillingPeriod>("annual");
    // A code typed and checked with the provider. It travels with the
    // purchase; the total on this screen stays the undiscounted one, because
    // what a discount comes to is the provider's arithmetic.
    const [coupon, setCoupon] = useState<Coupon | null>(null);
    // Paid modules ticked for this purchase. Empty is the ordinary case, and
    // then the quote the state already carries is the one shown.
    // Seeded from the link that got them here: pressing Adopt on a priced
    // module in the catalogue arrives as `?modules=alchely-meeting`, already
    // ticked, with the other one there to add. Read once — after that the
    // checkboxes are the source of truth, and a re-render must not put back
    // what somebody has just unticked.
    const [chosen, setChosen] = useState<string[]>(() =>
        (searchParams.get("modules") ?? "")
            .split(",")
            .map((slug) => slug.trim())
            .filter(Boolean)
    );
    // Hooks run before the early returns below, so what the quote is asked
    // for cannot depend on the state that has not loaded yet. Anything the
    // account cannot buy is dropped further down, before the checkout.
    const askingForQuote = chosen.length > 0 || coupon !== null;
    // Recomputed by the server on every tick. Never added up here: the annual
    // discount is applied per line, and a total assembled in the browser
    // would be a second opinion about somebody's invoice.
    const liveQuote = usePlusQuote(period, chosen, coupon?.code, askingForQuote, tier);

    if (isLoading) {
        return (
            <section
                aria-busy="true"
                className="rounded-xl border border-gray-200 p-5 dark:border-gray-700"
            >
                <p className="text-sm text-content-tertiary">
                    {t("subscription.billing.loading")}
                </p>
            </section>
        );
    }

    if (isError || !state) {
        return (
            <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <p role="alert" className="mb-3 text-sm text-red-600 dark:text-red-400">
                    {t("subscription.billing.loadError")}
                </p>
                <Button variant="outline" onClick={() => void refetch()}>
                    {t("subscription.billing.retry")}
                </Button>
            </section>
        );
    }

    const language = i18n.language || "es";
    const subscription = state.subscription;
    const cancelling = isCancelling(state);
    const periodEnd = subscription?.currentPeriodEnd;
    // A failed checkout is said under its own button, where the reader is
    // looking; these are the actions on a subscription already held.
    const actionFailed = cancel.isError || resume.isError || changeTier.isError;
    // The day a trial started now would first charge; null with no trial on offer.
    const trialEnds = trialChargeDate(state.trialDays);
    const currency = state.price?.currency ?? "eur";
    // Plus or Max, by name, for the sentences about what cancelling keeps.
    const planName = t(`subscription.billing.tier.${heldTier(state)}.name`);
    // What the state already knows, unless modules are ticked — then what the
    // server just priced. Falling back while that is in flight keeps the
    // figure on screen a real one rather than a blank.
    // A link can name anything. What is acted on is what this account can
    // actually buy, so a stale or mistyped slug in the URL cannot reach the
    // API and come back as a refusal of the whole purchase.
    const offerable = new Set(addableModules(state).map((module) => module.slug));
    const buying = chosen.filter((slug) => offerable.has(slug));
    const asking = buying.length > 0 || coupon !== null;
    const selectedQuote = asking
        ? liveQuote.data ?? quoteFor(state, period, tier)
        : quoteFor(state, period, tier);
    const offeredModules = addableModules(state);
    const toggleModule = (slug: string) =>
        setChosen((current) =>
            current.includes(slug)
                ? current.filter((other) => other !== slug)
                : [...current, slug]
        );

    return (
        <section className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
            <div className="mb-1 flex items-center gap-2">
                <CreditCard size={18} className="text-content-tertiary" />
                <h2 className="text-base font-semibold text-content-primary">
                    {t("subscription.billing.title")}
                </h2>
            </div>

            <p className="mb-4 text-sm text-content-tertiary">
                {/* The plan is announced, not only drawn: it is the one fact
                    that decides what the rest of the app will let them do. */}
                <span role="status">
                    {t(
                        `subscription.billing.plan.${
                            state.plan === "plus" ? heldTier(state) : "basic"
                        }`
                    )}
                </span>
            </p>

            {actionFailed && (
                <p role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:text-red-300">
                    {t(
                        changeTier.isError
                            ? checkoutErrorKey(changeTier.error)
                            : "subscription.billing.actionError"
                    )}
                </p>
            )}

            {/* --- Early adopter: nothing to buy, nothing to cancel ---- */}
            {state.earlyAdopter && (
                <div className="flex flex-col gap-3 rounded-xl bg-brand-50 p-4 dark:bg-brand-500/10">
                    <p className="text-sm font-semibold text-brand-700 dark:text-brand-300">
                        {t("subscription.billing.earlyAdopter.title")}
                    </p>
                    <p className="text-sm leading-relaxed text-content-secondary">
                        {t("subscription.billing.earlyAdopter.body")}
                    </p>
                </div>
            )}

            {/* --- Never subscribed ------------------------------------ */}
            {!state.earlyAdopter && subscription === null && state.plan === "basic" && (
                <div className="space-y-5">
                    {/* Before the prices, so they read as what is paid after
                        the trial rather than what is paid today. */}
                    {trialEnds && (
                        <p className="rounded-xl bg-brand-50 p-4 text-sm text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                            {t("subscription.billing.trial.intro", {
                                days: state.trialDays,
                                date: formatDate(trialEnds, language),
                            })}
                        </p>
                    )}
                    <TierPicker
                        state={state}
                        tier={tier}
                        period={period}
                        currency={currency}
                        language={language}
                        disabled={startCheckout.isPending}
                        onChange={setTier}
                    />

                    <fieldset className="space-y-3">
                        <legend className="flex items-center gap-2 text-sm font-semibold text-content-primary">
                            {t("subscription.billing.periodChoice")}
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800 dark:bg-green-500/15 dark:text-green-300">
                                {t("subscription.billing.annualSaving", {
                                    percent: ANNUAL_DISCOUNT_PERCENT,
                                })}
                            </span>
                        </legend>

                        <div className="flex flex-col gap-3 sm:flex-row">
                            {(["monthly", "annual"] as BillingPeriod[]).map((option) => {
                                const optionQuote = quoteFor(state, option, tier);
                                return (
                                    <label
                                        key={option}
                                        className={`flex flex-1 cursor-pointer items-start gap-3 rounded-xl border p-4 ${
                                            period === option
                                                ? "border-brand-500 bg-brand-50 dark:bg-brand-500/10"
                                                : "border-gray-200 dark:border-gray-700"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="billing-period"
                                            value={option}
                                            checked={period === option}
                                            onChange={() => setPeriod(option)}
                                            className="mt-1 accent-brand-500"
                                        />
                                        <span className="flex flex-col gap-0.5">
                                            <span className="text-sm font-semibold text-content-primary">
                                                {t(`subscription.billing.period.${option}`)}
                                            </span>
                                            {optionQuote && (
                                                <>
                                                    <span className="text-lg font-semibold text-content-primary">
                                                        {t("subscription.billing.perMonth", {
                                                            price: formatMoney(
                                                                optionQuote.monthlyEquivalentCents,
                                                                currency,
                                                                language
                                                            ),
                                                        })}
                                                        <span className="ml-1 text-xs font-normal text-content-tertiary">
                                                            {t("subscription.billing.tax.suffix")}
                                                        </span>
                                                    </span>
                                                    {option === "annual" && (
                                                        <span className="text-xs text-content-tertiary">
                                                            {t("subscription.billing.billedAnnually", {
                                                                price: formatMoney(
                                                                    optionQuote.totalCents,
                                                                    currency,
                                                                    language
                                                                ),
                                                            })}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </fieldset>

                    <ModulePicker
                        modules={offeredModules}
                        selected={chosen}
                        period={period}
                        currency={currency}
                        language={language}
                        disabled={startCheckout.isPending}
                        onToggle={toggleModule}
                    />

                    {selectedQuote && (
                        <QuoteBreakdown
                            quote={selectedQuote}
                            currency={currency}
                            language={language}
                        />
                    )}

                    <CouponField
                        applied={coupon}
                        disabled={startCheckout.isPending}
                        onCheck={(code) => checkCoupon.mutateAsync(code)}
                        onApplied={setCoupon}
                    />

                    <Button
                        onClick={() =>
                            startCheckout.mutate({
                                period,
                                coupon: coupon?.code,
                                modules: buying,
                                tier,
                            })
                        }
                        disabled={!canSubscribe(state) || startCheckout.isPending}
                    >
                        {/* Names the size being bought, so pressing it is not a
                            guess — and the trial, when the first one is on offer. */}
                        {trialEnds
                            ? t(
                                  tier === "max"
                                      ? "subscription.billing.trial.startMax"
                                      : "subscription.billing.trial.start",
                                  { days: state.trialDays }
                              )
                            : t(
                                  tier === "max"
                                      ? "subscription.billing.subscribeMax"
                                      : "subscription.billing.subscribe"
                              )}
                    </Button>
                    {trialEnds && (
                        <p className="text-xs text-content-tertiary">
                            {t("subscription.billing.trial.note", {
                                date: formatDate(trialEnds, language),
                            })}
                        </p>
                    )}
                    {startCheckout.isError && (
                        /* Under the button that failed, with its own reason
                           when it has one — a code this account does not
                           qualify for. */
                        <p
                            role="alert"
                            className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:text-red-300"
                        >
                            {t(checkoutErrorKey(startCheckout.error))}
                        </p>
                    )}
                    {!state.available && (
                        <p className="text-xs text-content-tertiary">
                            {t("subscription.billing.unavailable")}
                        </p>
                    )}
                </div>
            )}

            {/* --- Plus with nothing to bill ---------------------------- */}
            {!state.earlyAdopter && subscription === null && state.plan === "plus" && (
                <p className="text-sm text-content-secondary">
                    {t("subscription.billing.grantedElsewhere")}
                </p>
            )}

            {/* --- A live subscription --------------------------------- */}
            {subscription !== null && (
                <div className="space-y-4">
                    {subscription.status === "past_due" && (
                        <PaymentIssueBanner
                            message={t("subscription.billing.dunning.body")}
                            onUpdateCard={() => portal.mutate(true)}
                            isBusy={portal.isPending}
                        />
                    )}
                    {subscription.status !== "past_due" && subscription.cardExpiring && (
                        <PaymentIssueBanner
                            title={t("subscription.billing.cardExpiring.title")}
                            message={t("subscription.billing.cardExpiring.body")}
                            onUpdateCard={() => portal.mutate(true)}
                            isBusy={portal.isPending}
                        />
                    )}

                    {/* The first charge, on its own and large: the one date
                        somebody on a trial is looking for. */}
                    {subscription.inTrial && subscription.trialEndsAt && !cancelling && (() => {
                        const first = quoteFor(state, subscription.billingPeriod);
                        return (
                            <div className="rounded-xl bg-brand-50 p-4 dark:bg-brand-500/10">
                                <p className="text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
                                    {t("subscription.billing.trial.firstChargeLabel")}
                                </p>
                                <p className="mt-1 text-2xl font-semibold text-content-primary">
                                    {formatDate(subscription.trialEndsAt, language)}
                                </p>
                                <p className="mt-1 text-sm text-content-secondary">
                                    {first
                                        ? t("subscription.billing.trial.firstChargeAmount", {
                                              amount: formatMoney(payableCents(first), currency, language),
                                          })
                                        : t("subscription.billing.trial.firstChargeNoAmount")}
                                    {" "}
                                    <span className="text-content-tertiary">
                                        {t(`subscription.billing.onPeriod.${subscription.billingPeriod}`)}
                                    </span>
                                </p>
                            </div>
                        );
                    })()}

                    {!(subscription.inTrial && subscription.trialEndsAt && !cancelling) && (
                    <p className="text-sm text-content-secondary">
                        {periodEnd
                            ? t(
                                  cancelling
                                      ? "subscription.billing.endsOn"
                                      : "subscription.billing.renewsOn",
                                  { date: formatDate(periodEnd, language) }
                              )
                            : t(
                                  cancelling
                                      ? "subscription.billing.endsAtPeriodEnd"
                                      : "subscription.billing.renews"
                              )}
                        {" "}
                        <span className="text-content-tertiary">
                            {t(
                                `subscription.billing.onPeriod.${subscription.billingPeriod}`
                            )}
                        </span>
                    </p>
                    )}

                    {/* Plus or Max, and the way to the other one. */}
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                        <div className="text-sm">
                            <p className="font-semibold text-content-primary">
                                {t(`subscription.billing.tier.${subscription.heldTier}.name`)}
                                {" · "}
                                <span className="font-normal text-content-secondary">
                                    {t(`subscription.billing.tier.${subscription.heldTier}.sessions`)}
                                </span>
                            </p>
                            {subscription.tier !== subscription.heldTier &&
                                subscription.maxUntil && (
                                    <p className="text-xs text-content-tertiary">
                                        {t("subscription.billing.tier.maxUntil", {
                                            date: formatDate(subscription.maxUntil, language),
                                        })}
                                    </p>
                                )}
                        </div>
                        {subscription.status !== "canceled" && !cancelling && (
                            <Button
                                variant="outline"
                                onClick={() =>
                                    setConfirmingTier(subscription.tier === "max" ? "plus" : "max")
                                }
                                disabled={changeTier.isPending}
                            >
                                {t(
                                    subscription.tier === "max"
                                        ? "subscription.billing.tier.toPlus"
                                        : "subscription.billing.tier.toMax"
                                )}
                            </Button>
                        )}
                    </div>

                    {/* What the next charge is made of. A total that silently
                        includes a module adopted months ago is a surprise on a
                        card statement. */}
                    {(() => {
                        const current = quoteFor(state, subscription.billingPeriod);
                        return current ? (
                            <QuoteBreakdown
                                quote={current}
                                currency={currency}
                                language={language}
                            />
                        ) : null;
                    })()}

                    <div className="flex flex-wrap gap-3">
                        {cancelling ? (
                            <Button
                                onClick={() => resume.mutate()}
                                disabled={resume.isPending}
                                startIcon={<RotateCcw size={16} />}
                            >
                                {t("subscription.billing.resume")}
                            </Button>
                        ) : (
                            subscription.status !== "canceled" && (
                                <Button
                                    variant="outline"
                                    onClick={() => setConfirmingCancel(true)}
                                    disabled={cancel.isPending}
                                >
                                    {t("subscription.billing.cancel")}
                                </Button>
                            )
                        )}

                        <Button
                            variant="outline"
                            onClick={() => portal.mutate()}
                            disabled={portal.isPending}
                        >
                            {t("subscription.billing.manage")}
                        </Button>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={confirmingTier !== null}
                title={t(
                    confirmingTier === "max"
                        ? "subscription.billing.tier.confirmMax.title"
                        : "subscription.billing.tier.confirmPlus.title"
                )}
                // Up is charged today; down keeps Max until the renewal. Both
                // say the date, because the date is what changes.
                message={t(
                    confirmingTier === "max"
                        ? // Back up inside a period already paid at the Max
                          // price: nothing is charged, and it says so.
                          subscription?.heldTier === "max"
                            ? "subscription.billing.tier.confirmMax.messageAlreadyPaid"
                            : "subscription.billing.tier.confirmMax.message"
                        : "subscription.billing.tier.confirmPlus.message",
                    { date: periodEnd ? formatDate(periodEnd, language) : "" }
                )}
                confirmLabel={t(
                    confirmingTier === "max"
                        ? "subscription.billing.tier.confirmMax.confirm"
                        : "subscription.billing.tier.confirmPlus.confirm"
                )}
                isBusy={changeTier.isPending}
                onConfirm={() => {
                    if (confirmingTier) changeTier.mutate(confirmingTier);
                    setConfirmingTier(null);
                }}
                onCancel={() => setConfirmingTier(null)}
            />

            <CancelSubscriptionDialog
                isOpen={confirmingCancel}
                title={t("subscription.billing.cancelConfirm.title", { plan: planName })}
                // Says the date Plus actually stops: the whole point of
                // cancelling at period end is that nothing is lost today.
                message={
                    periodEnd
                        ? t("subscription.billing.cancelConfirm.messageWithDate", {
                              date: formatDate(periodEnd, language),
                              plan: planName,
                          })
                        : t("subscription.billing.cancelConfirm.message", { plan: planName })
                }
                isBusy={cancel.isPending}
                onConfirm={(why) => {
                    cancel.mutate(why);
                    setConfirmingCancel(false);
                }}
                onClose={() => setConfirmingCancel(false)}
            />
        </section>
    );
}

export type { PlusSubscriptionState };
